import type { ListBuilderConfiguration } from '../types';

/**
 * PD-16162 — the Sales Routes Schedule tab as a standard list.
 *
 * A row is one stop on one sales trip in the chosen window (a day or a week),
 * settled stops included, so the list reads as the whole day rather than what
 * is left of it. The rows come from `GET /sales-trips/schedule`, which the
 * Schedule's query function feeds with the `from` / `to` it works out from the
 * `scheduleWindow` filter; the other two filters travel as the usual filters
 * JSON. Sorting is the trip's own order — date, then time, then sequence — and
 * is not offered per column: a day's stops out of time order is not a view
 * anyone plans from.
 *
 * The actions column is the primary "Log Visit" for a stop still to visit and
 * the standard row actions for the other outcomes, all routed to the
 * Schedule's callbacks (`logVisit`, `skipStop`, `missStop`, `rescheduleStop`).
 * Opening the trip is the Trip column's link.
 */
export const SALES_TRIP_SCHEDULE_LIST_CONFIG: ListBuilderConfiguration = {
	objectType: 'sales_trip_stop',
	lists: [
		{
			id: 'default-sales-trip-schedule',
			cacheTTL: 60000,
			name: 'Schedule',
			type: 'default',
			active: true,
			columns: [
				{
					// PD-16184 — day over time in one column: the two are one fact
					// (when the rep is there), and the pair of columns they took
					// pushed the actions off the edge of the tab.
					id: 'when',
					headers: [{ id: 'when-header', text: 'When' }],
					fields: [
						{
							id: 'time-field',
							dataConfig: {
								type: 'field',
								// A clock reading ("1:00 PM") in the trip's own zone, added
								// to the row by the Schedule's query function; the raw
								// HH:mm the API carries is not how a rep reads a time.
								field: {
									fieldName: 'timeLabel',
									objectTable: 'sales_trip_stop',
								},
							},
							styleConfig: {
								size: 'md',
								fontWeight: 'semibold',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'when-break',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
						},
						{
							id: 'day-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'tripDate', objectTable: 'sales_trip' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
								formatOptions: { dateFormat: 'short' },
							},
							displayStyle: 'date',
						},
					],
					sortable: false,
					width: 110,
				},
				{
					id: 'account',
					headers: [{ id: 'account-header', text: 'Account' }],
					fields: [
						{
							id: 'account-name',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'clientName',
									objectTable: 'sales_trip_stop',
								},
							},
							styleConfig: {
								size: 'md',
								fontWeight: 'medium',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
							link: {
								enabled: true,
								type: 'custom',
								urlTemplate: '/clients/{clientId}',
							},
						},
						{
							id: 'account-break',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
						},
						{
							id: 'account-address',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'clientAddress',
									objectTable: 'sales_trip_stop',
								},
							},
							// PD-16184 — a long address wraps onto a second line in a
							// narrow column rather than being cut mid-word.
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
								allowTextWrap: true,
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					// The one column that gives: below this the address wraps rather
					// than the row scrolling sideways.
					minWidth: 180,
				},
				{
					id: 'status',
					headers: [{ id: 'status-header', text: 'Status' }],
					fields: [
						{
							id: 'status-badge',
							dataConfig: {
								type: 'field',
								// The status in words ("Still to visit"), added to the row
								// by the Schedule's query function.
								field: {
									fieldName: 'statusLabel',
									objectTable: 'sales_trip_stop',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: {
									type: 'status_map',
									statusMap: {
										'Still to visit': { type: 'info' },
										Visited: { type: 'success' },
										Skipped: { type: 'neutral' },
										Missed: { type: 'warning' },
										Rescheduled: { type: 'neutral' },
									},
								},
							},
							displayStyle: 'badge',
						},
						{
							id: 'status-break',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
						},
						{
							id: 'status-reason',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'outcomeReason',
									objectTable: 'sales_trip_stop',
								},
							},
							styleConfig: { size: 'xs', colorRule: { type: 'muted' } },
							displayStyle: 'text',
							conditionalVisibility: {
								field: 'outcomeReason',
								condition: 'is_not_null',
							},
						},
					],
					sortable: false,
					width: 130,
				},
				{
					// PD-16184 — the trip's name over the rep who drives it.
					id: 'trip',
					headers: [{ id: 'trip-header', text: 'Trip' }],
					fields: [
						{
							id: 'trip-name',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'tripName', objectTable: 'sales_trip' },
							},
							styleConfig: { size: 'sm', colorRule: { type: 'default' } },
							displayStyle: 'text',
							link: {
								enabled: true,
								type: 'custom',
								urlTemplate: '/sales-routes/trips/{salesTripId}',
							},
						},
						{
							id: 'trip-break',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
						},
						{
							id: 'salesperson-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'salespersonName',
									objectTable: 'sales_trip',
								},
							},
							styleConfig: { size: 'xs', colorRule: { type: 'muted' } },
							displayStyle: 'text',
						},
					],
					sortable: false,
					width: 190,
				},
				{
					id: 'actions',
					headers: [{ id: 'actions-header', text: '' }],
					fields: [
						{
							id: 'log-visit-action',
							dataConfig: { type: 'custom_text', customText: 'Log Visit' },
							styleConfig: {
								size: 'sm',
								iconName: 'check',
								variant: 'primary',
								colorRule: { type: 'default' },
							},
							displayStyle: 'button',
							link: {
								enabled: true,
								type: 'callback',
								callbackName: 'logVisit',
							},
							conditionalVisibility: {
								field: 'status',
								condition: 'equals',
								value: 'planned',
							},
						},
						{
							id: 'row-actions',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'skip-action',
									label: 'Skip',
									iconName: 'forward',
									callbackName: 'skipStop',
									visibility: 'conditional',
									conditionalVisibility: {
										field: 'status',
										condition: 'equals',
										value: 'planned',
									},
								},
								{
									id: 'missed-action',
									label: 'Missed',
									iconName: 'triangle-exclamation',
									callbackName: 'missStop',
									visibility: 'conditional',
									conditionalVisibility: {
										field: 'status',
										condition: 'equals',
										value: 'planned',
									},
								},
								{
									id: 'reschedule-action',
									label: 'Reschedule',
									iconName: 'calendar',
									callbackName: 'rescheduleStop',
									visibility: 'conditional',
									conditionalVisibility: {
										field: 'status',
										condition: 'equals',
										value: 'planned',
									},
								},
							],
						},
					],
					sortable: false,
					width: 130,
					align: 'right',
				},
			],
			defaultSortId: 'trip-order',
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 50,
		pageSizeOptions: [25, 50, 100, 200],
	},
	filters: {
		activeFilters: ['scheduleWindow', 'scheduleStatus', 'scheduleRep'],
		defaultFilters: {
			scheduleWindow: { id: 'today', label: 'Today' },
		},
	},
	objects: [
		{
			objectLabel: 'Sales Trip Stop',
			objectTable: 'sales_trip_stop',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'clientId', fieldLabel: 'Client ID' },
				{ fieldName: 'clientName', fieldLabel: 'Account' },
				{ fieldName: 'clientAddress', fieldLabel: 'Address' },
				{ fieldName: 'clientPhone', fieldLabel: 'Phone' },
				{ fieldName: 'plannedStartLocal', fieldLabel: 'Time (HH:mm)' },
				{ fieldName: 'timeLabel', fieldLabel: 'Time' },
				{ fieldName: 'statusLabel', fieldLabel: 'Status (label)' },
				{ fieldName: 'plannedDurationMinutes', fieldLabel: 'Duration (min)' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'outcomeReason', fieldLabel: 'Reason' },
				{ fieldName: 'sequence', fieldLabel: 'Stop number' },
				{ fieldName: 'lastVisitDate', fieldLabel: 'Last visit' },
			],
		},
		{
			objectLabel: 'Sales Trip',
			objectTable: 'sales_trip',
			selectedFields: [
				{ fieldName: 'salesTripId', fieldLabel: 'Trip ID' },
				{ fieldName: 'tripDate', fieldLabel: 'Day' },
				{ fieldName: 'tripName', fieldLabel: 'Trip' },
				{ fieldName: 'tripStatus', fieldLabel: 'Trip status' },
				{ fieldName: 'salespersonUserId', fieldLabel: 'Salesperson ID' },
				{ fieldName: 'salespersonName', fieldLabel: 'Salesperson' },
			],
		},
	],
	tableSorts: [
		{
			id: 'trip-order',
			// PD-16184 — named for what the reader sees, not for the mechanism.
			name: 'Earliest first',
			fields: [
				{ direction: 'ASC', fieldName: 'tripDate', objectTable: 'sales_trip' },
				{
					direction: 'ASC',
					fieldName: 'plannedStart',
					objectTable: 'sales_trip_stop',
				},
			],
		},
	],
};
